import { ButtonLink } from '@repo/shared/components'
import { useTranslate } from '@repo/shared/hooks'
import { useAppForm } from '@repo/ui/hooks'

import {
  useAgentCheck,
  useAgentEmailOtp,
  useAgentPhoneOtp,
} from '@repo/services'
import { useErrorHandler } from '@repo/shared/hooks'
import { useRegisterSchema } from '@repo/auth/schemas'
import { Cookie, handleNumericInput, handlePasteInput } from '@repo/tools'
import {
  Alert,
  Button,
  Form,
  FormField,
  FormItemWrapper,
  Icon,
  Input,
  Typography,
  toast,
} from '@repo/ui/components'
import { cn, normalizeDigits, normalizeMobile } from '@repo/ui/lib'
import { useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import { IRegisterForm, IRegisterInfo } from './register.types'
import { useLanguage } from '@repo/shared/hooks'
import { globalConfig } from '@repo/shared'

interface RegisterFormProps {
  registerInfo: IRegisterInfo | null
  onSuccess: (
    mobileOrEmail: string,
    invitationCode?: string,
    type?: 'email' | 'mobile',
    otp?: number,
  ) => void
}

export const RegisterForm = ({
  onSuccess,
  registerInfo,
}: RegisterFormProps) => {
  const { t, tRich } = useTranslate()
  const locale = useLanguage()

  const searchParams = useSearchParams()

  const referralCode =
    Cookie.get('referral_code') ?? searchParams.get('refCode')

  const [isEmailRegistration] = useState(registerInfo?.type === 'email')
  const phoneOtpMutation = useAgentPhoneOtp()
  const emailOtpMutation = useAgentEmailOtp()
  const { mobileSchema, emailSchema } = useRegisterSchema()
  const { errorHandler } = useErrorHandler()
  const { form } = useAppForm({
    formSchema: isEmailRegistration ? emailSchema : mobileSchema,
    defaultValues: {
      email: registerInfo?.mobileOrEmail,
      mobile: registerInfo?.mobileOrEmail?.replace('+98', '0'),
      invitationCode: referralCode,
    },
  })
  const checkUserMutation = useAgentCheck()

  const onSubmit = async (data: IRegisterForm) => {
    const isEmail = isEmailRegistration
    const identifier = isEmail
      ? (data.email ?? '')
      : normalizeMobile(data.mobile)
    const checkUserPayload = isEmail
      ? { email: identifier }
      : { phone: identifier }

    checkUserMutation.mutate(checkUserPayload, {
      onSuccess: (res) => {
        if (res?.exists) {
          toast.error({
            title: t('common_userAlreadyExists'),
          })
          return
        }
        if (isEmail) {
          emailOtpMutation.mutate(
            { email: identifier },
            {
              onSuccess: (res) => {
                const invitationCode = form.getValues('invitationCode')
                onSuccess(identifier, invitationCode, 'email', res?.data?.otp)
              },
              onError: errorHandler,
            },
          )
        } else {
          phoneOtpMutation.mutate(
            { phone: identifier },
            {
              onSuccess: (res) => {
                const invitationCode = form.getValues('invitationCode')
                onSuccess(identifier, invitationCode, 'mobile', res?.data?.otp)
              },
              onError: errorHandler,
            },
          )
        }
      },
      onError: errorHandler,
    })
  }

  useEffect(() => {
    form.setFocus(isEmailRegistration ? 'email' : 'mobile')
  }, [form, isEmailRegistration])

  return (
    <Form {...form}>
      <Alert
        color='neutral'
        caption={tRich.rich('agents_register_alert', {
          login: (chunks) => (
            <ButtonLink
              href={`${globalConfig.originWebUrl}/${locale}/auth/register`}
              color='base'
              size='sm'
              className='underline'
              target='_blank'
            >
              {chunks}
            </ButtonLink>
          ),
        })}
        icon={<Icon name='AlertTriangle' color='!fill-alert-main' />}
      />
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className='space-y-3xl w-full'
        noValidate
      >
        <FormField
          control={form.control}
          name={isEmailRegistration ? 'email' : 'mobile'}
          render={({ field }) => (
            <FormItemWrapper
              title={t(
                isEmailRegistration ? 'common_email' : 'common_mobileNumber',
              )}
              caption={
                isEmailRegistration
                  ? undefined
                  : t('loginPage_mobileStartsWithZero')
              }
              inputSize='lg'
            >
              <Input
                {...field}
                name={isEmailRegistration ? 'email' : 'mobile'}
                type='text'
                data-error={
                  !!form.formState.errors[
                    isEmailRegistration ? 'email' : 'mobile'
                  ]
                }
                onChange={(e) => {
                  if (!isEmailRegistration) {
                    const value = normalizeDigits(e.target.value)
                    field.onChange(value)
                  } else {
                    field.onChange(e)
                  }
                }}
                inputSize='lg'
                variant='fill'
                className={`ltr ${isEmailRegistration ? 'font-[AzarMehr]' : ''}`}
                autoComplete='off'
                onKeyDown={(e) => {
                  if (!isEmailRegistration) {
                    handleNumericInput(e, 11)
                  }
                }}
                onPaste={(e) => {
                  if (!isEmailRegistration) {
                    handlePasteInput(e, 11)
                  }
                }}
              />
            </FormItemWrapper>
          )}
        />

        <FormField
          control={form.control}
          name='invitationCode'
          disabled={referralCode ? true : false}
          render={({ field }) => (
            <FormItemWrapper
              title=''
              caption={
                referralCode
                  ? t('registerPage_invitationCodeDisabled')
                  : t('registerPage_invitationCodeOptional')
              }
              inputSize='lg'
            >
              <Input
                placeholder={t('registerPage_invitationCode')}
                autoComplete='off'
                inputSize='lg'
                variant={'fill'}
                className={cn(
                  form.watch('invitationCode') && 'ltr font-[AzarMehr]',
                )}
                {...field}
              />
            </FormItemWrapper>
          )}
        />

        <div className='flex items-center gap-xs'>
          <Typography size='sm' className='text-center text-neutral-surface-80'>
            {tRich.rich('registerPage_acceptTerms', {
              terms: (chunks) => (
                <ButtonLink
                  href={`${globalConfig.originWebUrl}/${locale}/agent-terms`}
                  color='base'
                  size='sm'
                  className='underline'
                  type='button'
                  target='_blank'
                  onClick={(e) => {
                    e.preventDefault()
                    // Force open in external browser
                    if (
                      (window.navigator as any)?.standalone ||
                      window.matchMedia('(display-mode: standalone)').matches
                    ) {
                      // PWA mode - open in external browser
                      window.location.href = `${globalConfig.originWebUrl}/${locale}/agent-terms`
                    } else {
                      // Normal mode - open in new tab
                      window.open(
                        `${globalConfig.originWebUrl}/${locale}/agent-terms`,
                        '_blank',
                        'noopener,noreferrer',
                      )
                    }
                  }}
                >
                  {chunks}
                </ButtonLink>
              ),
            })}
          </Typography>
        </div>

        <Button
          className='w-full'
          type='submit'
          variant='fill'
          size='lg'
          loading={
            phoneOtpMutation.isPending ||
            emailOtpMutation.isPending ||
            checkUserMutation.isPending
          }
        >
          {t('common_register')}
        </Button>
      </form>
    </Form>
  )
}
